import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Scroll, BookOpen, Feather, Star, Sun, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdventurePage = () => {
  const { token, refreshUser } = useAuth();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuests();
  }, []);

  const fetchQuests = async () => {
    try {
      const res = await fetch(`${API}/quests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuests(data);
      }
    } catch (err) {
      console.error('Failed to fetch quests:', err);
    } finally {
      setLoading(false);
    }
  };

  const completeQuest = async (questId) => {
    try {
      const res = await fetch(`${API}/quests/${questId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchQuests();
        await refreshUser();
      }
    } catch (err) {
      console.error('Failed to complete quest:', err);
    }
  };

  const getQuestIcon = (type) => {
    switch (type) {
      case 'books': return <BookOpen className="w-6 h-6" />;
      case 'words': return <Feather className="w-6 h-6" />;
      case 'quiz': return <Star className="w-6 h-6" />;
      case 'minutes': return <Sun className="w-6 h-6" />;
      default: return <Scroll className="w-6 h-6" />;
    }
  };

  const getQuestColor = (type) => {
    // Use theme variable for all quest types to maintain consistency
    return 'var(--th-md)';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-4xl animate-bounce">🎯</div>
      </div>
    );
  }

  const mainQuest = quests.find(q => q.type === 'books');
  const dailyQuests = quests.filter(q => q.type !== 'books');

  return (
    <div className="space-y-6 animate-slide-up" data-testid="adventure-page">
      {/* Page Hero */}
      <div 
        className="rounded-3xl p-6 text-center"
        style={{ 
          background: `linear-gradient(135deg, var(--th-md), var(--th-dk))`,
          color: 'white'
        }}
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <Scroll className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fredoka' }}>
            Today's Adventure
          </h1>
        </div>
        <p className="opacity-90">Finish your quests to grow your leaf points! 🌿</p>
      </div>

      {/* Main Quest */}
      {mainQuest && (
        <div 
          className="rounded-3xl p-6 border-4"
          style={{ 
            background: 'var(--card)',
            borderColor: getQuestColor(mainQuest.type)
          }}
        >
          <div className="flex items-start gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: getQuestColor(mainQuest.type), color: 'white' }}
            >
              {getQuestIcon(mainQuest.type)}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg" style={{ color: 'var(--text)' }}>
                {mainQuest.title}
              </h3>
              <p className="text-sm mb-3" style={{ color: 'var(--text-light)' }}>
                {mainQuest.desc}
              </p>
              
              <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: 'var(--border)' }}>
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min((mainQuest.progress / mainQuest.target) * 100, 100)}%`,
                    background: getQuestColor(mainQuest.type)
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: 'var(--text-light)' }}>
                  {mainQuest.progress} of {mainQuest.target} completed
                </span>
                {mainQuest.progress >= mainQuest.target && !mainQuest.completed && (
                  <button
                    onClick={() => completeQuest(mainQuest.id)}
                    data-testid="claim-main-quest"
                    className="px-4 py-2 rounded-full font-bold text-white btn-bounce"
                    style={{ background: getQuestColor(mainQuest.type) }}
                  >
                    Claim Reward! 🎉
                  </button>
                )}
                {mainQuest.completed && (
                  <span className="flex items-center gap-1 text-green-600 font-bold">
                    <CheckCircle className="w-5 h-5" /> Complete!
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Challenges */}
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text)', fontFamily: 'Fredoka' }}>
          Daily Challenges
        </h2>
        
        <div className="space-y-3">
          {dailyQuests.map(quest => (
            <div
              key={quest.id}
              data-testid={`quest-${quest.id}`}
              className={`rounded-2xl p-4 border-2 transition-all ${
                quest.completed ? 'opacity-75' : ''
              }`}
              style={{ 
                background: 'var(--card)',
                borderColor: quest.completed ? 'var(--primary)' : 'var(--border)'
              }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ 
                    background: quest.completed ? 'var(--primary)' : getQuestColor(quest.type), 
                    color: 'white' 
                  }}
                >
                  {quest.completed ? <CheckCircle className="w-6 h-6" /> : getQuestIcon(quest.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold" style={{ color: 'var(--text)' }}>
                    {quest.title}
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--text-light)' }}>
                    {quest.desc}
                  </p>
                  
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min((quest.progress / quest.target) * 100, 100)}%`,
                          background: quest.completed ? 'var(--primary)' : getQuestColor(quest.type)
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium flex-shrink-0" style={{ color: 'var(--text-light)' }}>
                      {quest.completed ? 'Done!' : `${quest.progress} of ${quest.target}`}
                    </span>
                  </div>
                </div>

                {quest.progress >= quest.target && !quest.completed && (
                  <button
                    onClick={() => completeQuest(quest.id)}
                    data-testid={`claim-${quest.id}`}
                    className="px-3 py-2 rounded-xl font-bold text-white text-sm btn-bounce flex-shrink-0"
                    style={{ background: getQuestColor(quest.type) }}
                  >
                    Claim! ⭐
                  </button>
                )}
                
                {quest.completed && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--primary)' }}>
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reward hint */}
      <div 
        className="rounded-2xl p-4 text-center"
        style={{ background: 'var(--background)' }}
      >
        <p className="text-sm" style={{ color: 'var(--text-light)' }}>
          Complete all quests to earn <span className="font-bold" style={{ color: 'var(--primary)' }}>bonus leaf points</span>! 🍃
        </p>
      </div>
    </div>
  );
};

export default AdventurePage;
